import { useInfiniteQuery } from '@tanstack/react-query';

import { useProjectsApi } from 'api/data/projects/projects-api';
import type { ApiDecompositionNode } from 'api/data/projects/types';
import { resolve } from 'api/resolve';

import { CHILDREN_PAGE_SIZE, CHILDREN_PEEK_LIMIT } from '../constants';
import { buildTreePage } from '../utils/buildTreePage';
import { toDecompositionNode } from '../utils/toDecompositionNode';
import { treeQueryKeys } from '../utils/treeQueryKeys';
import { useDecompositionRelation } from './useDecompositionRelation';

export function useChildrenInfiniteQuery(
  projectId: string,
  templateNodeId: string,
  parentDataNodeId: string,
  proposalId?: string,
  enabled = true
) {
  const { getTreeConnectedNodes } = useProjectsApi();
  const { relationId, technicalName } = useDecompositionRelation(templateNodeId);

  return useInfiniteQuery({
    queryKey: treeQueryKeys.children({ projectId, templateNodeId, proposalId }, parentDataNodeId),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const apiNodes: ApiDecompositionNode[] = await resolve(
        getTreeConnectedNodes(
          projectId,
          parentDataNodeId,
          relationId,
          technicalName,
          CHILDREN_PEEK_LIMIT,
          pageParam,
          proposalId
        )
      );
      return buildTreePage(
        apiNodes.map((n) => toDecompositionNode(n, parentDataNodeId)),
        pageParam,
        CHILDREN_PAGE_SIZE
      );
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextOffset : undefined),
    enabled: enabled && !!parentDataNodeId && !!projectId && !!templateNodeId && !!relationId && !!technicalName
  });
}
